// src/pages/About.js
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Grid,
    Stack,
    Divider,
    Button,
} from '@mui/material'
import {
    InfoOutlined as InfoIcon,
    Send as SendIcon,
    Payments as PayIcon,
    AccountBalance as BankIcon,
    AccountBalanceWallet as WalletIcon,
    VolunteerActivism as DonateIcon,
    Groups as GroupsIcon,
    Security as SecurityIcon,
    ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material'

import AnimatedSection from '../components/common/AnimatedSection'

// ✅ Best option: import from src
import googlePlayBadge from '../assets/badges/google-play-badge.png'
import appStoreBadge from '../assets/badges/app-store-badge.png'

const About = () => {
    const navigate = useNavigate()
    const { t, lang } = useLanguage()
    const isFr = lang === 'fr'
    const tt = (key, enFallback, frFallback) => t(key, null, isFr ? frFallback : enFallback)

    return (
        <Box sx={{ py: { xs: 5, md: 7 } }}>
            <Container maxWidth="lg">
                <AnimatedSection>
                    {/* Header */}
                    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1 }}>
                        <InfoIcon />
                        <Typography variant="h4" sx={{ fontWeight: 950, color: 'white' }}>
                            {tt('about.title', 'About Us', 'À propos')}
                        </Typography>
                    </Stack>

                    <Typography sx={{ color: 'rgba(255,255,255,0.72)', maxWidth: 900, mb: 3 }}>
                        {tt(
                            'about.subtitle',
                            'MojoPayr helps you move money and pay for things across Sierra Leone, Liberia, and Guinea. Fast transfers, clear fees, and simple ways to support people and projects back home.',
                            'MojoPayr vous aide à envoyer de l’argent et à payer des services en Sierra Leone, au Liberia et en Guinée. Des transferts rapides, des frais clairs et des moyens simples pour soutenir vos proches et des projets au pays.'
                        )}
                    </Typography>

                    {/* Hero card */}
                    <Card
                        sx={{
                            borderRadius: 6,
                            bgcolor: 'rgba(15,23,42,0.72)',
                            border: '1px solid rgba(255,255,255,0.10)',
                            mb: 3,
                        }}
                    >
                        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                            <Grid container spacing={2.5} alignItems="center">
                                <Grid item xs={12} md={8}>
                                    <Typography sx={{ color: 'white', fontWeight: 950, fontSize: 22, mb: 1 }}>
                                        {tt(
                                            'about.hero.headline',
                                            'Money moves better when it’s direct.',
                                            'L’argent circule mieux quand c’est direct.'
                                        )}
                                    </Typography>

                                    <Typography sx={{ color: 'rgba(255,255,255,0.72)', mb: 2 }}>
                                        {tt(
                                            'about.hero.p1',
                                            'When you send money with MojoPayr, we deliver to the right place on the other side: mobile money wallets or bank accounts, depending on what your recipient uses.',
                                            'Quand vous envoyez de l’argent avec MojoPayr, il arrive au bon endroit : portefeuille mobile money ou compte bancaire, selon ce que votre destinataire utilise.'
                                        )}
                                    </Typography>

                                    <Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>
                                        {tt(
                                            'about.hero.p2',
                                            'And paying for goods and services is simple. You don’t need to send money to someone and hope they buy it for you. Pay directly for items, bills, and services back home. No middle person.',
                                            'Et payer des biens et services est simple. Vous n’avez plus besoin d’envoyer de l’argent à quelqu’un en espérant qu’il achète à votre place. Payez directement des articles, factures et services au pays. Sans intermédiaire.'
                                        )}
                                    </Typography>

                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2.5 }}>
                                        <Button
                                            variant="contained"
                                            endIcon={<ArrowForwardIcon />}
                                            onClick={() => navigate('/register')}
                                            sx={{ borderRadius: 999, fontWeight: 900 }}
                                        >
                                            {tt('about.cta.createAccount', 'Create an account', 'Créer un compte')}
                                        </Button>

                                        <Button
                                            variant="outlined"
                                            onClick={() => navigate('/help-center')}
                                            sx={{ borderRadius: 999, fontWeight: 900 }}
                                        >
                                            {tt('about.cta.helpCenter', 'Visit Help Center', 'Aller au Centre d’aide')}
                                        </Button>
                                    </Stack>
                                </Grid>

                                <Grid
                                    item
                                    xs={12}
                                    md={4}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: { xs: 'center', md: 'flex-end' },
                                        pl: { md: 4 }, // ✅ pushes the circle to the right on desktop
                                        pr: { md: 0 },
                                    }}
                                >
                                    {/* “At a glance” pill */}
                                    <Card
                                        sx={{
                                            width: { xs: '100%', sm: 420, md: 420 }, // keeps space so nothing gets clipped
                                            borderRadius: 999,
                                            bgcolor: 'rgba(2,6,23,0.35)',
                                            border: '1px solid rgba(255,255,255,0.10)',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <CardContent sx={{ p: 0 }}>
                                            <Box
                                                sx={{
                                                    px: { xs: 3, md: 3.5 },
                                                    py: { xs: 3, md: 3.5 },
                                                }}
                                            >
                                                <Typography sx={{ color: 'white', fontWeight: 950, mb: 2, textAlign: 'center' }}>
                                                    {tt('about.glance.title', 'At a glance', 'En bref')}
                                                </Typography>

                                                <Box sx={{ display: 'grid', gap: 2 }}>
                                                    <GlanceRow
                                                        label={tt('about.glance.countries.label', 'Countries', 'Pays')}
                                                        value={tt(
                                                            'about.glance.countries.value',
                                                            'Sierra Leone, Liberia, Guinea',
                                                            'Sierra Leone, Liberia, Guinée'
                                                        )}
                                                    />
                                                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.10)' }} />
                                                    <GlanceRow
                                                        label={tt('about.glance.transfers.label', 'Transfers', 'Transferts')}
                                                        value={tt(
                                                            'about.glance.transfers.value',
                                                            'Fast delivery with clear fees',
                                                            'Livraison rapide avec des frais clairs'
                                                        )}
                                                    />
                                                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.10)' }} />
                                                    <GlanceRow
                                                        label={tt('about.glance.security.label', 'Security', 'Sécurité')}
                                                        value={tt(
                                                            'about.glance.security.value',
                                                            'Protected sessions and alerts',
                                                            'Sessions protégées et alertes'
                                                        )}
                                                    />
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* What we support */}
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} md={6}>
                            <Card
                                sx={{
                                    borderRadius: 6,
                                    bgcolor: 'rgba(15,23,42,0.72)',
                                    border: '1px solid rgba(255,255,255,0.10)',
                                    height: '100%',
                                }}
                            >
                                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1 }}>
                                        <SendIcon />
                                        <Typography sx={{ color: 'white', fontWeight: 950 }}>
                                            {tt(
                                                'about.cards.send.title',
                                                'Send money that lands where it should',
                                                'Envoyez de l’argent au bon endroit'
                                            )}
                                        </Typography>
                                    </Stack>

                                    <Typography sx={{ color: 'rgba(255,255,255,0.72)', mb: 2 }}>
                                        {tt(
                                            'about.cards.send.desc',
                                            'Send to mobile money or bank accounts. Your recipient gets funds in the way that fits their life.',
                                            'Envoyez vers mobile money ou compte bancaire. Votre destinataire reçoit selon ce qui lui convient.'
                                        )}
                                    </Typography>

                                    <Stack spacing={1.2}>
                                        <MiniLine
                                            icon={<WalletIcon />}
                                            text={tt(
                                                'about.cards.send.line1',
                                                'Mobile money delivery in supported countries',
                                                'Versement mobile money dans les pays pris en charge'
                                            )}
                                        />
                                        <MiniLine
                                            icon={<BankIcon />}
                                            text={tt(
                                                'about.cards.send.line2',
                                                'Bank transfers when a bank account is preferred',
                                                'Virements bancaires si un compte bancaire est préféré'
                                            )}
                                        />
                                        <MiniLine
                                            icon={<SecurityIcon />}
                                            text={tt(
                                                'about.cards.send.line3',
                                                'Receipts, history, and share links for proof',
                                                'Reçus, historique et liens de partage comme preuve'
                                            )}
                                        />
                                    </Stack>

                                    <Button
                                        variant="outlined"
                                        onClick={() => navigate('/send')}
                                        sx={{ mt: 2.5, borderRadius: 999, fontWeight: 900 }}
                                        endIcon={<ArrowForwardIcon />}
                                    >
                                        {tt('about.cards.send.cta', 'Try Send Money', 'Essayer Envoyer de l’argent')}
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card
                                sx={{
                                    borderRadius: 6,
                                    bgcolor: 'rgba(15,23,42,0.72)',
                                    border: '1px solid rgba(255,255,255,0.10)',
                                    height: '100%',
                                }}
                            >
                                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1 }}>
                                        <PayIcon />
                                        <Typography sx={{ color: 'white', fontWeight: 950 }}>
                                            {tt(
                                                'about.cards.pay.title',
                                                'Pay for goods and services directly',
                                                'Payez des biens et services directement'
                                            )}
                                        </Typography>
                                    </Stack>

                                    <Typography sx={{ color: 'rgba(255,255,255,0.72)', mb: 2 }}>
                                        {tt(
                                            'about.cards.pay.p1',
                                            'Paying for goods and services is simple. You don’t need to send money to anyone anymore to buy things back home.',
                                            'Payer des biens et services est simple. Vous n’avez plus besoin d’envoyer de l’argent à quelqu’un pour acheter au pays.'
                                        )}
                                    </Typography>

                                    <Typography sx={{ color: 'rgba(255,255,255,0.72)', mb: 2 }}>
                                        {tt(
                                            'about.cards.pay.p2',
                                            'Buying groceries for your family. Paying for a service. Supporting someone on a project. Pay directly. No middle person.',
                                            'Acheter des courses pour votre famille. Payer un service. Soutenir un projet. Payez directement. Sans intermédiaire.'
                                        )}
                                    </Typography>

                                    <Button
                                        variant="outlined"
                                        onClick={() => navigate('/payment')}
                                        sx={{ borderRadius: 999, fontWeight: 900 }}
                                        endIcon={<ArrowForwardIcon />}
                                    >
                                        {tt('about.cards.pay.cta', 'Pay Goods & Services', 'Payer biens et services')}
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card
                                sx={{
                                    borderRadius: 6,
                                    bgcolor: 'rgba(15,23,42,0.72)',
                                    border: '1px solid rgba(255,255,255,0.10)',
                                    height: '100%',
                                }}
                            >
                                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1 }}>
                                        <DonateIcon />
                                        <Typography sx={{ color: 'white', fontWeight: 950 }}>
                                            {tt(
                                                'about.cards.donations.title',
                                                'Donations that are easy to track',
                                                'Des dons faciles à suivre'
                                            )}
                                        </Typography>
                                    </Stack>

                                    <Typography sx={{ color: 'rgba(255,255,255,0.72)', mb: 2 }}>
                                        {tt(
                                            'about.cards.donations.desc',
                                            'Donate to causes you care about and keep clean records. Get receipts and share them when needed.',
                                            'Faites un don aux causes qui comptent pour vous et gardez des traces claires. Obtenez des reçus et partagez-les si besoin.'
                                        )}
                                    </Typography>

                                    <Button
                                        variant="outlined"
                                        onClick={() => navigate('/donations')}
                                        sx={{ borderRadius: 999, fontWeight: 900 }}
                                        endIcon={<ArrowForwardIcon />}
                                    >
                                        {tt('about.cards.donations.cta', 'Go to Donations', 'Aller aux dons')}
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card
                                sx={{
                                    borderRadius: 6,
                                    bgcolor: 'rgba(15,23,42,0.72)',
                                    border: '1px solid rgba(255,255,255,0.10)',
                                    height: '100%',
                                }}
                            >
                                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1 }}>
                                        <GroupsIcon />
                                        <Typography sx={{ color: 'white', fontWeight: 950 }}>
                                            {tt(
                                                'about.cards.contributions.title',
                                                'Contributions for groups and projects',
                                                'Contributions pour groupes et projets'
                                            )}
                                        </Typography>
                                    </Stack>

                                    <Typography sx={{ color: 'rgba(255,255,255,0.72)', mb: 2 }}>
                                        {tt(
                                            'about.cards.contributions.desc',
                                            'Run group contributions for a project, a family need, or a community goal. Share a link so people can contribute from anywhere.',
                                            'Organisez des contributions de groupe pour un projet, un besoin familial ou un objectif communautaire. Partagez un lien pour permettre de contribuer depuis n’importe où.'
                                        )}
                                    </Typography>

                                    <Button
                                        variant="outlined"
                                        onClick={() => navigate('/contributions')}
                                        sx={{ borderRadius: 999, fontWeight: 900 }}
                                        endIcon={<ArrowForwardIcon />}
                                    >
                                        {tt('about.cards.contributions.cta', 'Go to Contributions', 'Aller aux contributions')}
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Get the app */}
                    <Card
                        sx={{
                            mt: 3,
                            borderRadius: 6,
                            bgcolor: 'rgba(15,23,42,0.72)',
                            border: '1px solid rgba(255,255,255,0.10)',
                        }}
                    >
                        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                            <Typography sx={{ color: 'white', fontWeight: 950, textAlign: 'center', mb: 0.5 }}>
                                {tt('about.getApp.title', 'Get the app', 'Télécharger l’application')}
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.72)', textAlign: 'center', mb: 2.5 }}>
                                {tt(
                                    'about.getApp.subtitle',
                                    'Download the MojoPayr app for iOS and Android.',
                                    'Téléchargez MojoPayr pour iOS et Android.'
                                )}
                            </Typography>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="center">
                                <Box
                                    component="img"
                                    src={googlePlayBadge}
                                    alt="Get it on Google Play"
                                    sx={{
                                        height: 56,
                                        width: 'auto',
                                        cursor: 'pointer',
                                        display: 'block',
                                        filter: 'drop-shadow(0 10px 18px rgba(0,0,0,0.25))',
                                    }}
                                    onClick={() => window.open('https://play.google.com', '_blank', 'noreferrer')}
                                />

                                <Box
                                    component="img"
                                    src={appStoreBadge}
                                    alt="Download on the App Store"
                                    sx={{
                                        height: 56,
                                        width: 'auto',
                                        cursor: 'pointer',
                                        display: 'block',
                                        filter: 'drop-shadow(0 10px 18px rgba(0,0,0,0.25))',
                                    }}
                                    onClick={() => window.open('https://www.apple.com/app-store/', '_blank', 'noreferrer')}
                                />
                            </Stack>

                            <Typography
                                sx={{
                                    color: 'rgba(255,255,255,0.55)',
                                    textAlign: 'center',
                                    mt: 2,
                                    fontSize: 12.5,
                                }}
                            >
                                {tt(
                                    'about.getApp.note',
                                    'Store links are placeholders for now. We’ll update them when the apps are published.',
                                    'Les liens vers les stores sont provisoires. Nous les mettrons à jour quand les apps seront publiées.'
                                )}
                            </Typography>
                        </CardContent>
                    </Card>
                </AnimatedSection>
            </Container>
        </Box>
    )
}

const GlanceRow = ({ label, value }) => {
    return (
        <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontWeight: 900, fontSize: 13 }}>{label}</Typography>
            <Typography sx={{ color: 'white', fontWeight: 950, mt: 0.5 }}>{value}</Typography>
        </Box>
    )
}

const MiniLine = ({ icon, text }) => (
    <Stack direction="row" spacing={1.2} alignItems="center">
        <Box sx={{ color: 'rgba(255,255,255,0.85)' }}>{icon}</Box>
        <Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>{text}</Typography>
    </Stack>
)

export default About

